/**
 * Heatmap cell colors — Cobalt / Plasma model.
 *
 * Cells use a MUTED base only: each value is normalized to v ∈ [-1, 1]
 * (value / column-max) and mapped across the 7 --gv-heat-* stops by
 * t = (v+1)/2. The base is deliberately low-saturation so the field reads
 * quiet — we do NOT tint every cell by sign/magnitude.
 *
 * The signed accent hues (gold --gv-pos / magenta --gv-heat-neg) are
 * applied ONLY to the per-expiration nodes — the single largest long
 * (CALL WALL) and largest short (PUT WALL) in each column — by the
 * renderer, which overrides those cells to full hue + glow + ring.
 *
 * Stops are read from CSS custom properties at scale-creation time, so a
 * data-theme swap + re-render repaints with the new palette automatically.
 */

import { textOnCell } from './textOnCell';

// ── hex helpers (per brief) ───────────────────────────────
const hexToRgb = (h: string): number[] => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));

const rgbToHex = (a: number[]): string =>
  '#' + a.map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');

function mix(aHex: string, bHex: string, t: number): string {
  t = Math.max(0, Math.min(1, t));
  const a = hexToRgb(aHex), b = hexToRgb(bHex);
  return rgbToHex([0, 1, 2].map(i => a[i] + (b[i] - a[i]) * t));
}

function ramp(stops: string[], t: number): string {
  t = Math.max(0, Math.min(1, t));
  const x = t * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(x));
  const f = x - i;
  return mix(stops[i], stops[i + 1], f);
}

function readVar(name: string): string {
  if (typeof window === 'undefined') return '#000000';
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || '#000000';
}

const HEAT_VARS = [0, 1, 2, 3, 4, 5, 6].map(i => `--gv-heat-${i}`);

/**
 * Wall band: per expiration column, the largest long and largest short get
 * the accent — AND so does any same-sign cell within this fraction of that
 * peak |gamma|. So a strike sitting ~5% below the dominant short still reads
 * as a wall instead of vanishing into the muted base.
 */
export const WALL_PEAK_BAND = 0.05;

/**
 * Build a cell-color fn for a column normalized to `maxAbsValue`. Returns
 * a fn mapping a raw signed value → the MUTED base hex (no sign tint). The
 * per-column CALL/PUT wall cells are recolored to the accent hues by the
 * renderer. Stops are snapshotted once per call (cheap — once per column
 * per render); call again after a theme change to repaint.
 */
export function createCellColorScale(maxAbsValue: number) {
  const base = HEAT_VARS.map(readVar);
  const ceiling = maxAbsValue > 0 ? maxAbsValue : 1;

  return (value: number): string => {
    const v = Math.max(-1, Math.min(1, value / ceiling));
    return ramp(base, (v + 1) / 2);
  };
}

/**
 * On-cell label color. WCAG sRGB luminance picker (textOnCell), taking
 * the actual cell hex so it's correct on every step of the ramp. The
 * cellColor arg is optional only so the (dead) canvas callsites still
 * type-check; live renderers always pass the computed background.
 */
export function getCellTextColor(_value: number, _maxAbsValue: number, cellColor?: string): string {
  if (cellColor) return textOnCell(cellColor);
  return readVar('--gv-paper-hi');
}

/**
 * Heatmap cell label formatter. Uses M/B suffixes so a $465.7M dollar
 * exposure reads as "$465.7M" rather than "$465,700K" (which is what the
 * old K-only formatter did once we switched to dollar-notional GEX).
 *
 * We auto-pick decimal precision by magnitude:
 *   $1.2B    — 1 decimal at billion-scale (rare, fits column width)
 *   $465.7M  — 1 decimal at hundreds of millions
 *   $4.7M    — 1 decimal at single-digit millions (where rounding matters)
 *   $832K    — no decimal at thousands (cell labels would clip otherwise)
 */
export function formatExposureValue(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  if (abs >= 1)   return `${sign}$${abs.toFixed(0)}`;
  return `${sign}$${abs.toFixed(2)}`;
}
